import { useEffect, useState } from 'react';

import {
  createIndirectTaxFilingV2Document,
  submitIndirectTaxFilingV2,
} from '@commenda/api/indirect-tax/filings';
import { IndirectTaxFilingV2DocumentType } from '@commenda/api/indirect-tax/filings/types';
import { uploadSalesTaxFile } from '@commenda/api/new-sales-tax/files/upload';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  FileUploadStatus,
  Icons,
  SingleFileUploader,
} from '@commenda/kakashi';
import { FileBasic } from '@commenda/types/src/file/types';
import { LegacyDocumentType } from '@prisma/client';

interface IndirectTaxFilingVatConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  filingId: string;
  corporationId: number;
  companyId: number;
  onComplete: () => void;
}

const ACKNOWLEDGEMENT_FILE_CONFIG = {
  maxSize: 100 * 1024 * 1024, // 100MB
  acceptedTypes: {
    'application/pdf': ['.pdf'],
    'image/png': ['.png'],
    'image/jpeg': ['.jpg', '.jpeg'],
    'text/csv': ['.csv'],
    'application/vnd.ms-excel': ['.csv', '.xls'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
      '.xlsx',
    ],
  },
  message: 'PNG, JPG & PDF accepted (max. 100 MB)',
};

export const IndirectTaxFilingVatConfirmModal = ({
  isOpen,
  onClose,
  filingId,
  corporationId,
  companyId,
  onComplete,
}: IndirectTaxFilingVatConfirmModalProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The acknowledgement file is held here after selection (SingleFileUploader
  // stages it to S3) but is NOT registered to the filing until the agent
  // clicks Submit. Only one file is allowed because the backend stores a
  // single FILING_CONFIRMATION document per filing.
  const [acknowledgementFile, setAcknowledgementFile] =
    useState<FileBasic | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setAcknowledgementFile(null);
      setUploadError('');
      setError(null);
    }
  }, [isOpen]);

  // Register the attached acknowledgement (FILING_CONFIRMATION), then submit
  // the filing. If the upload/attach fails we abort before submitting and the
  // agent can retry submit.
  const onConfirmFiling = async () => {
    if (!acknowledgementFile) return;
    setIsSubmitting(true);
    setError(null);

    const uploaded = await uploadSalesTaxFile({
      file: acknowledgementFile,
      corporation_id: corporationId,
    });
    if (!uploaded.success || !uploaded.data?.data?.id) {
      setError(uploaded.error ?? 'Failed to upload acknowledgement');
      setIsSubmitting(false);
      return;
    }

    const created = await createIndirectTaxFilingV2Document({
      filingId,
      params: {
        corporationId,
        fileId: uploaded.data.data.id,
        documentType: IndirectTaxFilingV2DocumentType.FILING_CONFIRMATION,
        fileName: acknowledgementFile.fileName,
        fileUrl: acknowledgementFile.fileUrl,
      },
    });
    if (!created.success) {
      setError(created.error ?? 'Failed to attach acknowledgement');
      setIsSubmitting(false);
      return;
    }

    const result = await submitIndirectTaxFilingV2({ filingId, corporationId });
    setIsSubmitting(false);
    if (result.success) {
      onClose();
      onComplete();
    } else {
      setError(result.error ?? 'Failed to submit filing');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(v) => (v ? undefined : onClose())}>
      <DialogContent className="p-none flex flex-col gap-xl min-w-[440px]">
        <div className="p-3xl pb-none">
          <div className="size-12 rounded-md bg-utility-blue-50 flex justify-center items-center">
            <Icons.Send01 className="size-6 text-utility-blue-600" />
          </div>
        </div>
        <div className="px-3xl flex flex-col gap-xl">
          <div>
            <h3 className="txt-lg-bold text-text-primary mb-sm">
              Confirm filing
            </h3>
            <p className="txt-small-regular text-text-secondary">
              Attach the filing acknowledgement from the tax authority, then
              submit. The filing will be marked as filed and the service request
              will close.
            </p>
          </div>

          <div className="flex flex-col gap-sm">
            <span className="txt-small-medium text-text-secondary">
              Filing acknowledgement
            </span>
            <SingleFileUploader
              companyId={companyId}
              file={acknowledgementFile}
              setFile={setAcknowledgementFile}
              documentType={LegacyDocumentType.Other}
              maxSize={ACKNOWLEDGEMENT_FILE_CONFIG.maxSize}
              accept={ACKNOWLEDGEMENT_FILE_CONFIG.acceptedTypes}
              message={ACKNOWLEDGEMENT_FILE_CONFIG.message}
              onError={() =>
                setUploadError('File failed to upload. Please try again.')
              }
              setError={setUploadError}
              onUploadStatusChange={(status) =>
                setIsUploading(status === FileUploadStatus.UPLOADING)
              }
            />
            {uploadError && (
              <p className="txt-small-regular text-text-error">{uploadError}</p>
            )}
          </div>

          {error && (
            <p className="txt-small-regular text-text-error">{error}</p>
          )}
        </div>
        <DialogFooter>
          <Button
            size="sm"
            variant="secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            variant="primary"
            onClick={onConfirmFiling}
            disabled={isSubmitting || isUploading || !acknowledgementFile}
          >
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
